/**
 * Team API client (Phase 1.6).
 *
 * Wraps the 6 Team-tab HTTP endpoints under /v1/admin-review/team/.
 * Owner-gated server-side (requireOwnerRole); frontend gating via
 * canOwn(whoami) in src/lib/auth.ts is convenience, not security.
 */

import { writeFetch } from './writeFetch';
import type { UserTenantRole } from './types';
import { apiErrorFrom } from './api';

export interface TeamMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: UserTenantRole;
  is_active: boolean;
  last_sign_in_at: string | null;
}

export interface PendingGrant {
  email: string;
  role: UserTenantRole;
  display_name: string | null;
  created_at: string;
  created_by_display_name: string | null;
}

export interface TeamListResponse {
  members: TeamMember[];
  pending: PendingGrant[];
  request_id?: string;
}

export type BulkAddRowStatus =
  | 'will_grant'
  | 'will_queue'
  | 'already_member'
  | 'already_queued'
  | 'parse_error';

export interface BulkAddPreviewRow {
  line_no: number;
  email: string | null;
  role: UserTenantRole | null;
  display_name: string | null;
  status: BulkAddRowStatus;
  resolved_user_id: string | null;
  resolved_display_name: string | null;
  error_message: string | null;
}

export interface BulkAddPreviewResponse {
  rows: BulkAddPreviewRow[];
  summary: {
    will_grant: number;
    will_queue: number;
    already_member: number;
    already_queued: number;
    parse_error: number;
  };
}

export interface BulkAddCommitInput {
  rows: Array<{ email: string; role: UserTenantRole; display_name: string | null }>;
}

export interface BulkAddCommitResponse {
  granted_existing: number;
  queued_pending: number;
  already_member: number;
  already_queued: number;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

export const teamApi = {
  async list(tenantSlug: string): Promise<TeamListResponse> {
    return (await requestJson(`/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team`)) as TeamListResponse;
  },

  async previewBulkAdd(tenantSlug: string, paste: string): Promise<BulkAddPreviewResponse> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/bulk-add/preview`,
      { method: 'POST', body: JSON.stringify({ paste }) },
    )) as BulkAddPreviewResponse;
  },

  async commitBulkAdd(tenantSlug: string, input: BulkAddCommitInput): Promise<BulkAddCommitResponse> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/bulk-add/commit`,
      { method: 'POST', body: JSON.stringify(input) },
    )) as BulkAddCommitResponse;
  },

  /** `endCareTeams` is the Directory Editor door's confirm: the number of
   *  clients the office confirms the person loses access to, sent on the
   *  resubmission after the confirm and never on the first attempt. */
  async updateRole(tenantSlug: string, userId: string, role: UserTenantRole, endCareTeams?: number): Promise<TeamMember> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/${userId}/role`,
      { method: 'PATCH', body: JSON.stringify({ role, ...(endCareTeams === undefined ? {} : { end_care_teams: endCareTeams }) }) },
    )) as TeamMember;
  },

  async updateActive(tenantSlug: string, userId: string, isActive: boolean, endCareTeams?: number): Promise<TeamMember> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/${userId}/active`,
      { method: 'PATCH', body: JSON.stringify({ is_active: isActive, ...(endCareTeams === undefined ? {} : { end_care_teams: endCareTeams }) }) },
    )) as TeamMember;
  },

  /**
   * Hard-remove an unlinked login's membership (People-page "Remove" for
   * account_only stubs). Server refuses self-removal, the last active
   * owner, roster-linked logins, and memberships referenced by in-tenant
   * records (those can only be deactivated).
   */
  async remove(tenantSlug: string, userId: string): Promise<{ removed: string }> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/${userId}`,
      { method: 'DELETE' },
    )) as { removed: string };
  },

  async revokePending(tenantSlug: string, email: string): Promise<{ revoked: string }> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/admin-review/team/pending/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    )) as { revoked: string };
  },
};

